import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Calendar as CalendarIcon,
    User,
    Users,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Coffee
} from "lucide-react";
import { apiGet } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { getRole } from "../../utils/authService";
import socket from "../../utils/socket";
import { PageHeader, FilterBar, PillSelect } from "../common/ui.jsx";
import "./analytics.css";
import "./allAttendance.css";

const AllAttendance = () => {
    const navigate = useNavigate();
    // Managers get their own department-scoped endpoint — /ceo-dashboard is
    // restricted to admin/client/analytics_viewer/ceo/cfo server-side and would
    // 403 for them. /manager-dashboard returns the same response shape (both
    // built on performAnalyticsAggregation) but with `employees` pre-filtered
    // to only the agents in the manager's own managed department(s).
    const isManager = getRole() === "manager";
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [teamFilter, setTeamFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().split("T")[0]);
    const hasLoadedRef = useRef(false); // ref (not state) so live-update closures never see a stale value

    useEffect(() => {
        fetchAttendance();
    }, [dateFilter]);

    // Live updates: check-in (attendance_marked), check-out + status (agent_update)
    // both push here without needing a page refresh. Debounced since several
    // agents can emit agent_update in a quick burst (e.g. server restart).
    useEffect(() => {
        const isToday = dateFilter === new Date().toISOString().split("T")[0];
        if (!isToday) return;

        let debounceTimer = null;
        const handleLiveUpdate = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(fetchAttendance, 800);
        };
        socket.on("attendance_marked", handleLiveUpdate);
        socket.on("agent_update", handleLiveUpdate);
        return () => {
            clearTimeout(debounceTimer);
            socket.off("attendance_marked", handleLiveUpdate);
            socket.off("agent_update", handleLiveUpdate);
        };
    }, [dateFilter]);

    const fetchAttendance = async () => {
        if (!hasLoadedRef.current) setLoading(true);
        try {
            const endpoint = isManager ? "manager-dashboard" : "ceo-dashboard";
            const res = await apiGet(`/api/analytics/${endpoint}?filter=daily&date=${dateFilter}`);
            if (!res.ok) return;
            const result = await res.json().catch(() => null);
            if (!result?.success) return;

            const formatHm = (ms) => {
                const totalMins = Math.round(ms / 60_000);
                const h = Math.floor(totalMins / 60);
                const m = totalMins % 60;
                if (h <= 0) return `${m}m`;
                if (m === 0) return `${h}h`;
                return `${h}h ${m}m`;
            };

            // Standard shift length (e.g. 8h for a 09:00-17:00 shift). Overtime is sized
            // against this duration, not against wall-clock position — see below.
            const shiftHours = Number.isFinite(result.summary?.workHoursPerDay)
                ? result.summary.workHoursPerDay
                : (() => {
                    const [startH, startM] = String(result.summary?.workStart || "09:00").split(":").map(Number);
                    const [endH, endM] = String(result.summary?.workEnd || "17:00").split(":").map(Number);
                    const startMins = (Number.isFinite(startH) ? startH : 9) * 60 + (Number.isFinite(startM) ? startM : 0);
                    const endMins = (Number.isFinite(endH) ? endH : 17) * 60 + (Number.isFinite(endM) ? endM : 0);
                    return Math.max(0, (endMins - startMins) / 60);
                })();
            const shiftDurationMs = shiftHours * 3_600_000;

            // Employees created after the viewed date didn't exist yet — counting them
            // as "Absent" inflates the absent total for any historical date. Exclude
            // them entirely rather than showing a status that predates their hire.
            const list = (result.employees || [])
                .filter(emp => {
                    const createdAt = emp.details?.createdAt ? new Date(emp.details.createdAt) : null;
                    if (!createdAt || Number.isNaN(createdAt.getTime())) return true;
                    const createdDateStr = createdAt.getFullYear() + "-" + String(createdAt.getMonth() + 1).padStart(2, "0") + "-" + String(createdAt.getDate()).padStart(2, "0");
                    return createdDateStr <= dateFilter;
                })
                .map(emp => {
                const att = (emp.attendance || []).find(a => a.date === dateFilter);
                // Fallback for a day the employee was genuinely online but has no
                // check-in record (the check-in write can fail/be skipped) — same
                // fallback the HR org-wide attendance calendar already applies.
                const onlineDay = !att ? (emp.details?.dailyOnlineTime || []).find(d => d.date === dateFilter) : null;

                let status = "Absent";
                if (att?.isLeave) status = "Leave";
                else if (att?.isHoliday) status = "Holiday";
                else if (att) status = att.lateMinutes > 0 ? "Late" : "Present";
                else if (onlineDay?.onlineMs > 0) status = "Present";

                // Total Hours previously derived from check-in/check-out timestamps, which
                // left gaps whenever a check-in/out write was missed, delayed, or the agent
                // went "away" without a clean checkout. Source it instead from the same
                // tracked-worked-time field the performance section's "Time Worked" column
                // uses (workedMs, falling back to trackedActiveMs+trackedIdleMs, then
                // totalOnlineMs) — see getEmployeeWorkedMs in AnalyticsDashboard.jsx and
                // toEmployeeWorkedMs in analyticsReportService.js for the same fallback chain.
                const totalMs = Number(emp?.workedMs) > 0
                    ? Number(emp.workedMs)
                    : (Number(emp?.trackedActiveMs || 0) + Number(emp?.trackedIdleMs || 0)) > 0
                        ? Number(emp.trackedActiveMs || 0) + Number(emp.trackedIdleMs || 0)
                        : Number(emp?.totalOnlineMs || onlineDay?.onlineMs || 0);

                // Overtime = tracked worked time beyond the standard shift length (target),
                // same rule the performance section's work-status calculation applies.
                const overtimeMs = Math.max(0, totalMs - shiftDurationMs);

                return {
                    id: emp.agentId,
                    name: displayName(emp),
                    department: emp.departmentName || "Unassigned",
                    checkIn: att?.checkInTime
                        ? new Date(att.checkInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                        : "—",
                    checkOut: att?.checkOutTime
                        ? new Date(att.checkOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                        : "—",
                    status,
                    totalHours: formatHm(totalMs),
                    overtime: overtimeMs > 0 ? formatHm(overtimeMs) : "—",
                };
            });

            setData(list);
        } catch (err) {
            console.error("Failed to fetch attendance:", err);
        } finally {
            hasLoadedRef.current = true;
            setLoading(false);
        }
    };

    // Derived from the already-fetched rows rather than a separate
    // /api/departments call — this only ever lists teams that actually have
    // someone in today's (or the viewed date's) attendance, not every team
    // in the org, and needs no extra request.
    const teams = useMemo(
        () => Array.from(new Set(data.map(d => d.department))).sort(),
        [data]
    );

    const filteredData = data.filter(item => {
        const matchesSearch =
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.department.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            statusFilter === "all" || item.status.toLowerCase() === statusFilter.toLowerCase();
        const matchesTeam =
            teamFilter === "all" || item.department === teamFilter;
        return matchesSearch && matchesStatus && matchesTeam;
    });

    const presentCount  = data.filter(r => r.status === "Present" || r.status === "Late").length;
    const absentCount   = data.filter(r => r.status === "Absent").length;
    const leaveCount    = data.filter(r => r.status === "Leave").length;

    const getStatusBadge = (status) => {
        switch (status) {
            case "Present":
                return <span className="status-badge status-present"><CheckCircle2 size={12} /> Present</span>;
            case "Late":
                return <span className="status-badge status-late"><AlertCircle size={12} /> Late</span>;
            case "Leave":
                return <span className="status-badge status-late"><Coffee size={12} /> On Leave</span>;
            case "Holiday":
                return <span className="status-badge"><AlertCircle size={12} /> Holiday</span>;
            default:
                return <span className="status-badge status-absent"><XCircle size={12} /> Absent</span>;
        }
    };

    return (
        <div className="attendance-view">
            <PageHeader
                title={isManager ? "Team Attendance" : "All Employees Attendance"}
                subtitle={isManager ? "Daily presence and work hours for your team" : "Daily presence and work hours across the organisation"}
                actions={
                    <div className="date-picker-wrapper">
                        <CalendarIcon size={16} className="filter-icon" />
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                        />
                    </div>
                }
            />

            <div className="summary-cards">
                <div className="summary-card present">
                    <Users size={24} />
                    <div className="card-info">
                        <span className="card-value">{data.length}</span>
                        <span className="card-label">{isManager ? "Team Members" : "Total Employees"}</span>
                    </div>
                </div>
                <div className="summary-card leave">
                    <CheckCircle2 size={24} />
                    <div className="card-info">
                        <span className="card-value">{presentCount}</span>
                        <span className="card-label">Present Today</span>
                    </div>
                </div>
                <div className="summary-card holiday">
                    <Coffee size={24} />
                    <div className="card-info">
                        <span className="card-value">{leaveCount}</span>
                        <span className="card-label">On Leave</span>
                    </div>
                </div>
                <div className="summary-card absent">
                    <XCircle size={24} />
                    <div className="card-info">
                        <span className="card-value">{absentCount}</span>
                        <span className="card-label">Absent</span>
                    </div>
                </div>
            </div>

            <div className="gc-card">
                <FilterBar
                    search={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search employees or departments…"
                >
                    <PillSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        <option value="present">Present</option>
                        <option value="late">Late</option>
                        <option value="absent">Absent</option>
                        <option value="holiday">Holiday</option>
                    </PillSelect>
                    {teams.length > 1 && (
                        <PillSelect value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
                            <option value="all">All Teams</option>
                            {teams.map(team => (
                                <option key={team} value={team}>{team}</option>
                            ))}
                        </PillSelect>
                    )}
                </FilterBar>

                <div className="gc-table-wrap">
                    <table className="gc-table">
                        <thead>
                            <tr>
                                <th>Employee Name</th>
                                <th>Department</th>
                                <th>Check-in</th>
                                <th>Last Seen</th>
                                <th>Status</th>
                                <th>Total Worked Hours</th>
                                <th>Overtime</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={i} className="skeleton-row">
                                        <td colSpan="7"><div className="skeleton-line"></div></td>
                                    </tr>
                                ))
                            ) : filteredData.length > 0 ? (
                                filteredData.map((row) => (
                                    <tr key={row.id} onClick={() => navigate(`/analytics/employee/${row.id}`)} style={{ cursor: "pointer" }}>
                                        <td>
                                            <div className="employee-info">
                                                <div className="avatar-mini"><User size={14} /></div>
                                                <span style={{ color: "var(--accent-primary)", fontWeight: 600 }}>{row.name}</span>
                                            </div>
                                        </td>
                                        <td>{row.department}</td>
                                        <td>{row.checkIn}</td>
                                        <td>{row.checkOut}</td>
                                        <td>{getStatusBadge(row.status)}</td>
                                        <td className="hours-cell">{row.totalHours}</td>
                                        <td className="hours-cell" style={row.overtime !== "—" ? { color: "var(--color-warning, #f39c12)", fontWeight: 700 } : undefined}>{row.overtime}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="empty-state">
                                        No records found for the selected filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AllAttendance;
